import { LoadingOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React from 'react';
import styled from 'styled-components/macro';
import { useEntityRegistry } from '../../useEntityRegistry';
import { SearchBar } from '../SearchBar';
import { useEnterKeyListener } from '../../shared/useEnterKeyListener';
import { useTranslation } from '../../../i18n/useTranslation';

const StyledButton = styled(Button)`
    margin: 5px 12px;
    text-align: center;
    justify-content: center;
    width: calc(100% - 24px);
`;

export const DropdownMenu = styled.div<{ alignRight?: boolean }>`
    background-color: white;
    border-radius: 5px;
    box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    min-width: 200px;

    ${(props) =>
        props.alignRight &&
        `
    position: absolute;
    left: 205px;
    top: -34px;
    `}

    .ant-dropdown-menu-title-content {
        background-color: white;
        &:hover {
            background-color: white;
        }
    }

    .ant-dropdown-menu-item {
        height: unset !important;
    }
`;

const ScrollableContent = styled.div`
    max-height: 312px;
    overflow: auto;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 9px;

    svg {
        height: 16px;
        width: 16px;
    }
`;

interface Props {
    menu: React.ReactNode;
    updateFilters: () => void;
    field?: string;
    isLoading: boolean;
    searchQuery: string;
    updateSearchQuery: (query: string) => void;
    alignRight?: boolean;
    searchPlaceholder?: string;
}

export default function OptionsDropdownMenu({
    menu,
    field,
    updateFilters,
    isLoading,
    searchQuery,
    updateSearchQuery,
    alignRight,
    searchPlaceholder,
}: Props) {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    useEnterKeyListener({ querySelectorToExecuteClick: '#updateFiltersButton' });

    return (
        <DropdownMenu alignRight={alignRight} data-testid="filter-dropdown">
            <ScrollableContent>
                <SearchBar
                    filterFocus
                    field={field}
                    initialQuery={searchQuery}
                    placeholderText={t(searchPlaceholder || '')}
                    suggestions={[]}
                    hideRecommendations
                    style={{
                        padding: 12,
                        paddingBottom: 5,
                    }}
                    inputStyle={{
                        height: 30,
                        fontSize: 14,
                        borderRadius: 8,
                    }}
                    onSearch={() => null}
                    onQueryChange={(q) => updateSearchQuery(q)}
                    entityRegistry={entityRegistry}
                />
                {React.cloneElement(menu as React.ReactElement, {
                    style: { boxShadow: 'none' },
                    tabIndex: -1,
                })}
                {isLoading && (
                    <LoadingWrapper>
                        <LoadingOutlined />
                    </LoadingWrapper>
                )}
            </ScrollableContent>
            <StyledButton
                tabIndex={0}
                id={`updateFiltersButton-${field}`} // for accessibility keyboard navigation
                type="primary"
                role="menuitem"
                onClick={updateFilters}
                data-testid="update-filters"
            >
                {t('Update')}
            </StyledButton>
        </DropdownMenu>
    );
}
